import { useState, useEffect } from 'react';
import { storage, auth } from '../lib/firebase';
import { ref, listAll, getDownloadURL, deleteObject, uploadBytes } from 'firebase/storage';
import { toast } from 'sonner';

export interface Wallpaper {
  url: string;
  name: string;
  timestamp: number;
}

export function useWallpapers() {
  const [wallpapers, setWallpapers] = useState<Wallpaper[]>([]);
  const [loading, setLoading] = useState(true);

  const loadWallpapers = async () => {
    if (!auth.currentUser) {
      setWallpapers([]);
      setLoading(false);
      return;
    }

    try {
      const wallpapersRef = ref(storage, `wallpapers/${auth.currentUser.uid}`);
      const result = await listAll(wallpapersRef);

      if (result.items.length === 0) {
        setWallpapers([]);
        setLoading(false);
        return;
      }

      const wallpaperPromises = result.items.map(async (item) => {
        try {
          const url = await getDownloadURL(item);
          
          // Précharger l'image
          await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = resolve;
            img.onerror = reject;
            img.src = url;
          });
          
          const nameParts = item.name.split('-');
          const timestamp = parseInt(nameParts[0], 10);
          return {
            url,
            name: item.name,
            timestamp
          };
        } catch (error) {
          return null;
        }
      });

      const loadedWallpapers = (await Promise.all(wallpaperPromises))
        .filter((w): w is Wallpaper => w !== null)
        .sort((a, b) => b.timestamp - a.timestamp);

      setWallpapers(loadedWallpapers);
    } catch (error) {
      toast.error('Erreur lors du chargement des fonds d\'écran');
    } finally {
      setLoading(false);
    }
  };

  const deleteWallpaper = async (filename: string) => {
    if (!auth.currentUser) return;

    const toastId = toast.loading('Suppression du fond d\'écran en cours...');

    try {
      const fileRef = ref(storage, `wallpapers/${auth.currentUser.uid}/${filename}`);
      await deleteObject(fileRef);
      
      setWallpapers(current => current.filter(w => w.name !== filename));
      
      toast.success('Fond d\'écran supprimé', { id: toastId });
    } catch (error) {
      toast.error('Erreur lors de la suppression', { id: toastId });
    }
  };

  const saveWallpaper = async (blob: Blob) => {
    if (!auth.currentUser) return null;

    const toastId = toast.loading('Sauvegarde en cours...');

    try {
      const timestamp = Date.now();
      const filename = `${timestamp}-wallpaper.png`;

      if (wallpapers.length >= 3) {
        const oldestWallpaper = wallpapers[wallpapers.length - 1];
        await deleteWallpaper(oldestWallpaper.name);
      }

      const wallpaperRef = ref(storage, `wallpapers/${auth.currentUser.uid}/${filename}`);
      const file = new File([blob], filename, { type: 'image/png' });
      await uploadBytes(wallpaperRef, file);
      const url = await getDownloadURL(wallpaperRef);

      // Précharger l'image avant de l'ajouter à l'état
      await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });

      const newWallpaper = { url, name: filename, timestamp };
      
      setWallpapers(current => {
        const updated = [...current.filter(w => w.name !== filename), newWallpaper]
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 3);
        return updated;
      });
      
      toast.success('Fond d\'écran sauvegardé', { id: toastId });
      return url;
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde', { id: toastId });
      return null;
    }
  };

  const countWallpapers = async (): Promise<number> => {
    return wallpapers.length;
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        loadWallpapers();
      } else {
        setWallpapers([]);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  return {
    wallpapers,
    loading,
    deleteWallpaper,
    saveWallpaper,
    loadWallpapers,
    countWallpapers
  };
}